/**
 * 평가요소 예시 제안 — 활동명(성취기준) textarea에 `[코드]` 줄이 있을 때만 보이는 조용한 한 줄 액션.
 *
 * 흐름: "성취기준으로 평가요소 예시 만들기"(같은 폼의 "성취기준 찾기"·"학교평가운영계획 첨부" 행과 같은
 * 인라인 점선 버튼 패턴, docs/achievement_standards_ux.md D2) → 스켈레톤 → 제안 카드 3개(탭하면 평가요소에
 * 들어감) → "다시 만들기".
 * 실패하면 짧은 안내 + "다시 시도". 요청 중에는 버튼이 사라져 중복 요청이 생기지 않는다.
 * 성취기준 코드가 바뀌면(줄 추가·삭제) 이전 제안은 버리고 처음 상태로 돌아간다.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  EVALUATION_ELEMENTS_ERROR_CODE,
  suggestEvaluationElements,
} from '../../api/evaluationElementsApi';
import { normalizeCode, recordCurriculum } from './achievementStandardPicker.utils';
import './EvaluationElementsSuggest.css';

const CODE_LINE_RE = /^\s*(\[[^\]\n]+\])/;
const SKELETON_ROWS = [0, 1, 2];

const SparkIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    focusable="false"
    height="14"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="14"
  >
    <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" />
    <path d="M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8L19 16z" />
  </svg>
);

export const STATUS = {
  DONE: 'done',
  ERROR: 'error',
  IDLE: 'idle',
  LOADING: 'loading',
};

/** 활동명 textarea에서 `[코드]`로 시작하는 줄의 코드를 순서대로 뽑는다(정규화 기준 중복 제거). */
export const extractStandardCodes = (text) => {
  const codes = [];
  const seen = new Set();
  String(text || '').split('\n').forEach((line) => {
    const matched = line.match(CODE_LINE_RE);
    if (!matched) return;
    const normalized = normalizeCode(matched[1]);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    codes.push(matched[1]);
  });
  return codes;
};

/**
 * 코드 → 서버 refs. 선택 목록(`selectedStandards`)에 같은 코드가 있으면 key/curriculum을 붙이고,
 * 교사가 직접 붙여 넣은 코드는 code만 보낸다(서버가 학교급 샤드 안에서 찾는다).
 */
export const buildSuggestRefs = (activityText, selectedStandards = [], { curriculum } = {}) => {
  const byCode = new Map();
  (Array.isArray(selectedStandards) ? selectedStandards : []).forEach((record) => {
    const normalized = normalizeCode(record?.code);
    if (normalized && record?.key && !byCode.has(normalized)) byCode.set(normalized, record);
  });
  const fallbackCurriculum = curriculum
    || (byCode.size > 0 ? recordCurriculum(byCode.values().next().value) : recordCurriculum(null));
  return extractStandardCodes(activityText).map((code) => {
    const record = byCode.get(normalizeCode(code));
    if (record) {
      return { code: record.code || code, curriculum: recordCurriculum(record), key: record.key };
    }
    return { code, curriculum: fallbackCurriculum };
  });
};

/** 평가요소 textarea 삽입: 비어 있으면 대체, 있으면 줄바꿈 후 추가. 같은 문장이 이미 있으면 그대로 둔다. */
export const insertSuggestion = (currentValue, suggestion) => {
  const existing = String(currentValue || '');
  const next = String(suggestion || '').trim();
  if (!next) return existing;
  if (!existing.trim()) return next;
  if (existing.split('\n').some((line) => line.trim() === next)) return existing;
  return `${existing.replace(/\s+$/g, '')}\n${next}`;
};

const refsSignatureOf = (refs) => refs.map((ref) => `${ref.curriculum}:${ref.key || `code:${normalizeCode(ref.code)}`}`).join('|');

const MAX_NOTICE_CODES = 2;

/** `[9수99-98]`처럼 코드 표기만 남긴다. key(`middle-수학:[9수01-01]`)가 오면 안의 코드를 쓴다. */
const displayCode = (input) => {
  const text = String(input || '');
  const matched = text.match(/\[[^[\]\n]{1,40}\]/);
  return matched ? matched[0] : text;
};

const joinCodes = (items) => {
  const codes = [];
  items.forEach((item) => {
    const code = displayCode(item?.input);
    if (code && !codes.includes(code)) codes.push(code);
  });
  const shown = codes.slice(0, MAX_NOTICE_CODES).join(', ');
  const more = codes.length > MAX_NOTICE_CODES ? ` 외 ${codes.length - MAX_NOTICE_CODES}개` : '';
  return `${shown}${more}`;
};

/**
 * 결과 위에 한 줄로 알릴 문구. 고시 원문을 못 찾아 교사 문장으로 만든 코드와, 문장도 없어 빠진 코드를 구분한다.
 * 둘 다 없으면 빈 문자열(안내 없음).
 */
export const buildGroundingNotice = ({ teacher_grounded_refs: teacher = [], dropped_refs: dropped = [] } = {}) => {
  const parts = [];
  if (Array.isArray(teacher) && teacher.length > 0) {
    parts.push(`${joinCodes(teacher)}는 고시 원문을 찾지 못해 적어주신 문장으로 만들었어요.`);
  }
  if (Array.isArray(dropped) && dropped.length > 0) {
    parts.push(`${joinCodes(dropped)}는 문장이 없어 빼고 만들었어요. 코드 뒤에 문장을 적으면 함께 반영돼요.`);
  }
  return parts.join(' ');
};

const EvaluationElementsSuggest = ({
  activityText,
  category = '교과',
  curriculum,
  onInsert,
  schoolLevel,
  selectedStandards = [],
  subject,
  value,
}) => {
  const refs = useMemo(
    () => buildSuggestRefs(activityText, selectedStandards, { curriculum }),
    [activityText, curriculum, selectedStandards],
  );
  const refsSignature = refsSignatureOf(refs);
  const [status, setStatus] = useState(STATUS.IDLE);
  const [suggestions, setSuggestions] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [notice, setNotice] = useState('');
  const [insertedIndexes, setInsertedIndexes] = useState([]);
  const requestRef = useRef(false);
  const abortRef = useRef(null);

  // 성취기준 줄이 바뀌면 이전 제안은 더 이상 근거가 맞지 않으니 버린다(진행 중 요청도 끊는다).
  useEffect(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    requestRef.current = false;
    setStatus(STATUS.IDLE);
    setSuggestions([]);
    setErrorMessage('');
    setNotice('');
    setInsertedIndexes([]);
  }, [refsSignature]);

  useEffect(() => () => {
    abortRef.current?.abort();
  }, []);

  if (refs.length === 0) return null;

  const run = async () => {
    if (requestRef.current) return;
    requestRef.current = true;
    const controller = new AbortController();
    abortRef.current = controller;
    setStatus(STATUS.LOADING);
    setErrorMessage('');
    setNotice('');
    setInsertedIndexes([]);
    setSuggestions([]);

    const result = await suggestEvaluationElements(
      { activityText, category, refs, schoolLevel, subject },
      { signal: controller.signal },
    );
    if (abortRef.current !== controller || controller.signal.aborted) return;
    requestRef.current = false;
    abortRef.current = null;
    if (!result.ok) {
      if (result.error?.code === EVALUATION_ELEMENTS_ERROR_CODE.CANCELLED) return;
      setErrorMessage(result.error?.message || '예시를 만들지 못했어요. 다시 시도해주세요.');
      setStatus(STATUS.ERROR);
      return;
    }
    setSuggestions(result.data.suggestions);
    setNotice(buildGroundingNotice(result.data));
    setStatus(STATUS.DONE);
  };

  const handleInsert = (index) => {
    const suggestion = suggestions[index];
    if (!suggestion) return;
    onInsert?.(insertSuggestion(value, suggestion), suggestion);
    setInsertedIndexes((prev) => (prev.includes(index) ? prev : [...prev, index]));
  };

  return (
    <div className="ees" data-testid="ees">
      {status === STATUS.IDLE && (
        <button
          className="ees__action"
          data-testid="ees-suggest"
          onClick={run}
          type="button"
        >
          <SparkIcon />
          <span>
            성취기준으로
            {' '}
            <b>평가요소 예시</b>
            {' '}
            만들기
          </span>
        </button>
      )}

      {status === STATUS.LOADING && (
        <div aria-live="polite" className="ees__panel" role="status">
          <p className="ees__hint">성취기준을 읽고 예시를 만들고 있어요</p>
          <ul className="ees__list">
            {SKELETON_ROWS.map((row) => (
              <li className="ees__skeleton" data-testid="ees-skeleton" key={row} />
            ))}
          </ul>
        </div>
      )}

      {status === STATUS.DONE && (
        <div className="ees__panel" data-testid="ees-results">
          <div className="ees__head">
            <p className="ees__hint">탭하면 평가요소에 들어가요</p>
            <button className="ees__ghost" data-testid="ees-retry" onClick={run} type="button">
              다시 만들기
            </button>
          </div>
          {notice && (
            <p className="ees__notice" data-testid="ees-notice">{notice}</p>
          )}
          <ul className="ees__list">
            {suggestions.map((suggestion, index) => {
              const inserted = insertedIndexes.includes(index);
              return (
                <li key={suggestion}>
                  <button
                    aria-pressed={inserted}
                    className={`ees__card${inserted ? ' ees__card--inserted' : ''}`}
                    data-testid={`ees-card-${index}`}
                    onClick={() => handleInsert(index)}
                    type="button"
                  >
                    <span className="ees__card-text">{suggestion}</span>
                    {inserted && <span className="ees__card-done">넣었어요</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {status === STATUS.ERROR && (
        <div className="ees__panel ees__panel--error" role="alert">
          <p className="ees__error" data-testid="ees-error">{errorMessage}</p>
          <button className="ees__ghost" data-testid="ees-retry" onClick={run} type="button">
            다시 시도
          </button>
        </div>
      )}
    </div>
  );
};

export default EvaluationElementsSuggest;
